/**
 * taskflow-timeline-math.ts
 *
 * Timeline Coordinate Projections and SVG Bézier Dependency Curve Geometry.
 * Converts temporal dates to viewport pixel coordinates across Day/Week/Month zoom scales
 * and renders smooth cubic Bézier curves between tasks.
 */

export type TimelineZoomLevel = 'day' | 'week' | 'month';

export interface BarDimensions {
  x: number;
  y: number;
  width: number;
  height: number;
}

export class TimelineMathEngine {
  private static readonly PIXELS_PER_DAY: Record<TimelineZoomLevel, number> = {
    day: 50,
    week: 20,
    month: 6,
  };

  /**
   * Convert date string to X pixel coordinate relative to base start date
   */
  static dateToPixel(
    dateStr: string,
    baseStartDateStr: string,
    zoomLevel: TimelineZoomLevel
  ): number {
    const targetMs = new Date(dateStr).getTime();
    const baseMs = new Date(baseStartDateStr).getTime();

    if (isNaN(targetMs) || isNaN(baseMs)) return 0;

    const diffDays = (targetMs - baseMs) / (1000 * 60 * 60 * 24);
    const pxPerDay = this.PIXELS_PER_DAY[zoomLevel];

    return Math.round(diffDays * pxPerDay);
  }

  /**
   * Calculate Gantt bar X coordinate and width
   */
  static calculateBarCoordinates(params: {
    startDate?: string;
    endDate?: string;
    dueDate?: string;
    baseStartDate: string;
    zoomLevel: TimelineZoomLevel;
  }): { x: number; width: number } {
    const pxPerDay = this.PIXELS_PER_DAY[params.zoomLevel];
    const defaultSpanDays = 1;

    let start = params.startDate;
    let end = params.endDate;

    if (!start && params.dueDate) {
      // Single milestone on due date
      const x = this.dateToPixel(params.dueDate, params.baseStartDate, params.zoomLevel);
      return { x, width: Math.max(pxPerDay, 24) };
    }

    if (!start) {
      return { x: 0, width: pxPerDay * defaultSpanDays };
    }

    const x = this.dateToPixel(start, params.baseStartDate, params.zoomLevel);

    if (end) {
      const startMs = new Date(start).getTime();
      const endMs = new Date(end).getTime();
      const days = Math.max(1, (endMs - startMs) / (1000 * 60 * 60 * 24));
      return { x, width: Math.round(days * pxPerDay) };
    }

    return { x, width: pxPerDay * defaultSpanDays };
  }

  /**
   * Generates SVG cubic Bézier path connecting two Gantt bars
   * Format: `M startX startY C cp1X cp1Y cp2X cp2Y endX endY`
   */
  static calculateDependencyBezier(fromBar: BarDimensions, toBar: BarDimensions): string {
    const startX = fromBar.x + fromBar.width;
    const startY = fromBar.y + fromBar.height / 2;

    const endX = toBar.x;
    const endY = toBar.y + toBar.height / 2;

    // Standard forward dependency
    if (endX >= startX) {
      const dx = Math.max(20, (endX - startX) / 2);
      const cp1X = startX + dx;
      const cp1Y = startY;
      const cp2X = endX - dx;
      const cp2Y = endY;

      return `M ${startX} ${startY} C ${cp1X} ${cp1Y} ${cp2X} ${cp2Y} ${endX} ${endY}`;
    }

    // Inverted backwards dependency (loop around)
    const loopOffset = 25;
    const cp1X = startX + loopOffset;
    const cp1Y = startY + (endY >= startY ? 20 : -20);
    const cp2X = endX - loopOffset;
    const cp2Y = endY + (endY >= startY ? -20 : 20);

    return `M ${startX} ${startY} C ${cp1X} ${cp1Y} ${cp2X} ${cp2Y} ${endX} ${endY}`;
  }
}
